import CloseIcon from '@mui/icons-material/Close';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import dabaduIcon from 'assets/images/trade-in/dabadu-icon.png';
import { TRADEIN_MODAL_CLOSE } from 'constants/CommonConstants';
import useQueryParams from 'hooks/UseQueryParams';
import i18n from 'i18next';
import React, { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import DynamicStepperForm from 'sharedComponents/dynamicStepperForm';
import { StepConfigItem } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CommonButton } from 'sharedComponents/index';
import { DefaultModal } from 'sharedComponents/modal';
import { verifyOtp } from 'store/CommonModuleState';
import { clearLeadCreation, resetStepCount } from 'store/TradeInState';
import { RootState } from 'store/store';
import { tradeInStepConfig1, tradeInStepConfig2, tradeInStepConfig3, tradeInStepConfig4 } from './StepConfig';
import { MODULE_NAME } from './TradeInConstant';
import { IReqVerifyOtp } from './TradeInInterface';
import TradeinHeader from './header/TradeinHeader';
import TradeinV3Header from './header/TradeinV3Header';
import './trade-in.scss';

const TradeIn: React.FC = () => {
  const { t } = useTranslation();

  const hasCalledMounted = React.useRef(false);

  const queryParams = useQueryParams();
  const [initialStep, setInitialStep] = useState(0);
  const [isSuccessModal, setIsSuccessModal] = useState(false);
  const [eventDataObj, setEventDataObj] = useState({});
  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const type = queryParams.get('type');
  const version = queryParams.get('version');

  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};
  const widgetData = authenticateClientData?.data?.widgetData || {};
  const showChat = widgetData?.showChat || 'FALSE';
  const gtmID = widgetData?.gtmID || '';

  useEffect(() => {
    i18n.changeLanguage(authenticateClientData?.data?.widgetData?.language || 'en');

    if (gtmID) {
      // GTM initialize on component mount
      const tagManagerArgs = {
        gtmId: gtmID,
      };

      TagManager.initialize(tagManagerArgs);
    }
  }, [authenticateClientData, gtmID]);

  useEffect(() => {
    if (hasCalledMounted.current) return;
    hasCalledMounted.current = true;

    document.body.classList.add('tradein-modal-open');
    if (window.self !== window.top) {
      document.body.classList.add('iframe-html');
    }
  }, []);

  // // Example Usage
  // const handleWidgetOpen = () => {
  //   sendEventToParent('widgetOpened', { status: 'tradein authenticate' });
  // };

  // const handleWidgetClose = () => {
  //   sendEventToParent('widgetClosed', { status: 'closed' });
  // };

  const onCloseModal = () => {
    window.parent.postMessage({ type: TRADEIN_MODAL_CLOSE, data: { close: true }, res: { module: 'trade-in' } }, '*');
    dispatch(resetStepCount());
    dispatch(clearLeadCreation());
    localStorage.setItem(`${MODULE_NAME}-currentStep`, '0');
    localStorage.removeItem(MODULE_NAME);
  };

  const clearLocalStorage = () => {
    localStorage.setItem(`${MODULE_NAME}-currentStep`, '0');
    localStorage.removeItem(MODULE_NAME);

    setIsSuccessModal(true);
    setInitialStep(1);
  };

  // Step configurations mapped by type and version
  const stepConfigsByTypeAndVersion: {
    [type: string]: { [version: string]: StepConfigItem[] };
  } = {
    banner: {
      '1.0': tradeInStepConfig2(t),
      '1.1': tradeInStepConfig3(t),
    },
    button: {
      '1.0': tradeInStepConfig1(t),
      '1.1': tradeInStepConfig4(t),
    },
    embed: {
      '1.0': tradeInStepConfig1(t),
      '1.1': tradeInStepConfig4(t),
    },
  };

  const stepHeaderByTypeAndVersion: {
    [type: string]: { [version: string]: React.ReactElement };
  } = {
    banner: {
      '1.0': <TradeinHeader title={t('stepperFormTradeIn.yourTradeInValue')} />,
      '1.1': <TradeinV3Header title={t('stepperFormTradeIn.yourTradeInValue')} type={type} version={version} />,
    },
    button: {
      '1.0': <TradeinHeader title={t('stepperFormTradeIn.yourTradeInValue')} />,
      '1.1': <TradeinV3Header title={t('stepperFormTradeIn.yourTradeInValue')} type={type} version={version} />,
    },
    embed: {
      '1.0': <TradeinHeader title={t('stepperFormTradeIn.yourTradeInValue')} />,
      '1.1': <TradeinV3Header title={t('stepperFormTradeIn.yourTradeInValue')} type={type} version={version} />,
    },
  };

  const handleFinalSubmit: SubmitHandler<FieldValues> = async (formData) => {
    // Perform the final API call using formData

    const finalData: IReqVerifyOtp = {
      phoneNumber: formData?.confirmMobile,
      otp: formData?.otp,
    };

    setEventDataObj({
      vendor: 'dabadu',
      item_year: formData?.vehicleSearch?.year,
      item_make: formData?.vehicleSearch?.make,
      item_model: formData?.vehicleSearch?.model,
      first_name: formData?.firstName,
      last_name: formData?.lastName,
      phone: formData?.phone,
      email: formData?.email,
    });

    dispatch(verifyOtp(finalData)).then((res) => {
      if (Object.keys(res?.payload).length && res?.payload?.message?.valid && res?.payload?.success) {
        clearLocalStorage();
      }
    });
  };

  const optimyWidget = () => {
    const messageData = {
      type: 'iframeFormSubmitted',
      formName: 'Trade In',
      eventDataObj: eventDataObj,
    };

    window.parent.postMessage(messageData, '*');
  };

  // Function to get the step configuration based on type and version
  const getStepConfig = (type: string | null, version: string | null): StepConfigItem[] => {
    if (!type || !version) {
      console.error('Invalid type or version');
      return [];
    }

    const typeConfig = stepConfigsByTypeAndVersion[type];
    if (!typeConfig) {
      console.error('Unknown type:', type);
      return [];
    }

    const config = typeConfig[version];
    if (!config) {
      console.error(`No configuration found for type "${type}" and version "${version}"`);
      return [];
    }

    return config;
  };

  const getStepHeader = (type: string | null, version: string | null) => {
    if (!type || !version) {
      console.error('Invalid type or version');
      return null;
    }

    const typeConfig = stepHeaderByTypeAndVersion[type];
    if (!typeConfig) {
      console.error('Unknown type:', type);
      return null;
    }

    const config = typeConfig[version];
    if (!config) {
      console.error(`No configuration found for type "${type}" and version "${version}"`);
      return null;
    }

    return config;
  };

  // Retrieve step configuration based on the type and version
  const stepConfig = getStepConfig(type, version);
  const stepHeaderByVersion = getStepHeader(type, version);

  return (
    <>
      <div className='container'>
        <div className='trade-in-stepper'>
          <header className='trade-in-stepper__header'>
            <img src={dabaduIcon} alt='Dabadu' className='img-fluid' />
            {window.self !== window.top && (
              <span className='close' onClick={onCloseModal}>
                <CloseIcon />
              </span>
            )}
          </header>

          <main className='trade-in-stepper__body px-0'>
            {version != null ? (
              <DynamicStepperForm
                stepConfig={stepConfig}
                initialStep={initialStep}
                onFinish={handleFinalSubmit}
                moduleConfig={{
                  boxShadow: false,
                  stepperHeader: stepHeaderByVersion,
                  name: MODULE_NAME,
                }}
              />
            ) : null}
          </main>

          {/* <footer className='trade-in-stepper__footer'>
            <div className='powered-by'>
              <div className='powered-by__lbl'>{t('stepperForm.poweredBy')}</div>
              <img src={dabaduDarkLogo} alt='Dabadu' className='img-fluid' />
            </div>
          </footer> */}
        </div>
      </div>

      <DefaultModal
        open={isSuccessModal}
        handleClose={() => {
          setIsSuccessModal(false);
          onCloseModal();
        }}
        customClass='lead-gen-thankyou-modal'
        childern={
          <div className='trade-in-stepper__otp-complete__content text-center'>
            <div className='trade-in-stepper__otp-complete__content__icon'>
              <TaskAltIcon color='primary' sx={{ fontSize: 60 }} />
            </div>
            <h4 className='trade-in-stepper__otp-complete__content__title'>
              {t('stepperFormTradeIn.tradeInSuccessfully')}
            </h4>
            {showChat?.toUpperCase() === 'TRUE' ? (
              <div className='pt-3'>
                <CommonButton
                  type={'button'}
                  label={t('stepperFormTradeIn.connectNow')}
                  minWidth='180px'
                  onClick={optimyWidget}
                />
              </div>
            ) : null}
          </div>
        }
      />
    </>
  );
};

export default TradeIn;
